import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  TextField,
  Button,
  BlockStack,
  Divider,
  Text,
  Banner,
  InlineStack,
  Select,
  Checkbox
} from '@shopify/polaris';
import axios from 'axios';
import { AppContext } from '../App';

function SettingsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { shop, setShop, showToast, API } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);

  // Sender address
  const [senderName, setSenderName] = useState('');
  const [senderAddress1, setSenderAddress1] = useState('');
  const [senderAddress2, setSenderAddress2] = useState('');
  const [senderCity, setSenderCity] = useState('');
  const [senderState, setSenderState] = useState('');
  const [senderPostcode, setSenderPostcode] = useState('');
  const [senderCountry, setSenderCountry] = useState('US');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderEmail, setSenderEmail] = useState('');

  // Default package dimensions
  const [defaultWeight, setDefaultWeight] = useState('1.0');
  const [defaultLength, setDefaultLength] = useState('20.0');
  const [defaultWidth, setDefaultWidth] = useState('15.0');
  const [defaultHeight, setDefaultHeight] = useState('10.0');

  // Shipping preferences (Shippo strategies)
  const [defaultStrategy, setDefaultStrategy] = useState('cheapest');
  const [domesticStrategy, setDomesticStrategy] = useState('cheapest');
  const [internationalStrategy, setInternationalStrategy] = useState('cheapest');
  const [preferredCarrier, setPreferredCarrier] = useState('');
  const [autoFulfillOnPaid, setAutoFulfillOnPaid] = useState(true);

  const strategyOptions = [
    { label: 'Cheapest Rate', value: 'cheapest' },
    { label: 'Fastest Delivery', value: 'fastest' },
    { label: 'Best Value (Balance)', value: 'best_value' },
    { label: 'Preferred Carrier', value: 'preferred' }
  ];

  const carrierOptions = [
    { label: 'No Preference', value: '' },
    { label: 'USPS', value: 'usps' },
    { label: 'UPS', value: 'ups' },
    { label: 'FedEx', value: 'fedex' },
    { label: 'DHL Express', value: 'dhl_express' },
    { label: 'Australia Post', value: 'australia_post' },
    { label: 'Canada Post', value: 'canada_post' },
    { label: 'Royal Mail', value: 'royal_mail' }
  ];

  const countryOptions = [
    { label: 'United States', value: 'US' },
    { label: 'Australia', value: 'AU' },
    { label: 'Canada', value: 'CA' },
    { label: 'United Kingdom', value: 'GB' },
    { label: 'Germany', value: 'DE' },
    { label: 'France', value: 'FR' },
    { label: 'India', value: 'IN' },
    { label: 'Japan', value: 'JP' },
    { label: 'Other', value: 'OTHER' }
  ];

  useEffect(() => {
    const shopParam = searchParams.get('shop') || localStorage.getItem('shop');
    if (shopParam) {
      setShop(shopParam);
      fetchStoreSettings(shopParam);
      fetchShippingPreferences(shopParam);
    } else {
      navigate('/');
    }
  }, []);

  const fetchStoreSettings = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/store`, {
        params: { shop: shopDomain }
      });
      const settings = response.data.settings || {};
      setSenderName(settings.sender_name || '');
      setSenderAddress1(settings.sender_address_line1 || '');
      setSenderAddress2(settings.sender_address_line2 || '');
      setSenderCity(settings.sender_city || settings.sender_suburb || '');
      setSenderState(settings.sender_state || '');
      setSenderPostcode(settings.sender_postcode || '');
      setSenderCountry(settings.sender_country || 'US');
      setSenderPhone(settings.sender_phone || '');
      setSenderEmail(settings.sender_email || '');
      setDefaultWeight(String(settings.default_weight || 1.0));
      setDefaultLength(String(settings.default_length || 20.0));
      setDefaultWidth(String(settings.default_width || 15.0));
      setDefaultHeight(String(settings.default_height || 10.0));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to fetch settings', true);
    }
  };

  const fetchShippingPreferences = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/shipping/preferences`, {
        params: { shop: shopDomain }
      });
      const prefs = response.data || {};
      setDefaultStrategy(prefs.default_strategy || 'cheapest');
      setDomesticStrategy(prefs.domestic_strategy || 'cheapest');
      setInternationalStrategy(prefs.international_strategy || 'cheapest');
      setPreferredCarrier(prefs.preferred_carrier || '');
      setAutoFulfillOnPaid(prefs.auto_fulfill_on_paid !== false);
    } catch (err) {
      console.error('Failed to fetch shipping preferences');
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/store/settings`,
        {
          sender_name: senderName,
          sender_address_line1: senderAddress1,
          sender_address_line2: senderAddress2,
          sender_city: senderCity,
          sender_suburb: senderCity,
          sender_state: senderState,
          sender_postcode: senderPostcode,
          sender_country: senderCountry,
          sender_phone: senderPhone,
          sender_email: senderEmail,
          default_weight: parseFloat(defaultWeight),
          default_length: parseFloat(defaultLength),
          default_width: parseFloat(defaultWidth),
          default_height: parseFloat(defaultHeight)
        },
        {
          params: { shop: shop }
        }
      );
      showToast('Settings saved successfully!');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save settings', true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveShippingPreferences = async () => {
    setShippingLoading(true);
    try {
      await axios.put(
        `${API}/shipping/preferences`,
        {
          shop: shop,
          default_strategy: defaultStrategy,
          domestic_strategy: domesticStrategy,
          international_strategy: internationalStrategy,
          preferred_carrier: preferredCarrier,
          auto_fulfill_on_paid: autoFulfillOnPaid
        },
        {
          params: { shop: shop }
        }
      );
      showToast('Shipping preferences saved!');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save preferences', true);
    } finally {
      setShippingLoading(false);
    }
  };

  return (
    <Page
      title="Settings"
      backAction={{ onAction: () => navigate(`/orders?shop=${shop}`) }}
    >
      <BlockStack gap="400">
        <Banner tone="info">
          <p>Configure your sender address, default package dimensions, and shipping rate preferences for global shipping with Shippo.</p>
        </Banner>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Shipping Rate Strategy</Text>
            <Text variant="bodySm" as="p" tone="subdued">
              Choose how ShipMate selects shipping rates. Powered by Shippo's multi-carrier network.
            </Text>
            <Divider />
            <Checkbox
              label="Auto-create labels when orders are paid"
              checked={autoFulfillOnPaid}
              onChange={setAutoFulfillOnPaid}
              helpText="Automatically generate shipping labels via webhooks when an order is marked as paid"
              data-testid="auto-fulfill-checkbox"
            />
            <Select
              label="Default Strategy"
              options={strategyOptions}
              value={defaultStrategy}
              onChange={setDefaultStrategy}
              helpText="Applied when no specific rule matches"
              data-testid="default-strategy-select"
            />
            <Select
              label="Domestic Shipping Strategy"
              options={strategyOptions}
              value={domesticStrategy}
              onChange={setDomesticStrategy}
              helpText="For shipments within the same country"
              data-testid="domestic-strategy-select"
            />
            <Select
              label="International Shipping Strategy"
              options={strategyOptions}
              value={internationalStrategy}
              onChange={setInternationalStrategy}
              helpText="For cross-border shipments"
              data-testid="international-strategy-select"
            />
            <Select
              label="Preferred Carrier"
              options={carrierOptions}
              value={preferredCarrier}
              onChange={setPreferredCarrier}
              helpText="Used when strategy is 'Preferred Carrier'"
              data-testid="preferred-carrier-select"
            />
            <Button
              onClick={handleSaveShippingPreferences}
              loading={shippingLoading}
              data-testid="save-shipping-prefs-btn"
            >
              Save Shipping Preferences
            </Button>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Sender Address</Text>
            <Divider />
            <TextField
              label="Sender Name / Business Name"
              value={senderName}
              onChange={setSenderName}
              autoComplete="off"
              data-testid="sender-name-input"
            />
            <TextField
              label="Address Line 1"
              value={senderAddress1}
              onChange={setSenderAddress1}
              autoComplete="off"
              data-testid="sender-address1-input"
            />
            <TextField
              label="Address Line 2"
              value={senderAddress2}
              onChange={setSenderAddress2}
              autoComplete="off"
              data-testid="sender-address2-input"
            />
            <InlineStack gap="200">
              <div style={{ flex: 1 }}>
                <TextField
                  label="City"
                  value={senderCity}
                  onChange={setSenderCity}
                  autoComplete="off"
                  data-testid="sender-city-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="State / Province"
                  value={senderState}
                  onChange={setSenderState}
                  autoComplete="off"
                  data-testid="sender-state-input"
                />
              </div>
            </InlineStack>
            <InlineStack gap="200">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Postal / ZIP Code"
                  value={senderPostcode}
                  onChange={setSenderPostcode}
                  autoComplete="off"
                  data-testid="sender-postcode-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <Select
                  label="Country"
                  options={countryOptions}
                  value={senderCountry}
                  onChange={setSenderCountry}
                  data-testid="sender-country-select"
                />
              </div>
            </InlineStack>
            <InlineStack gap="200">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Phone"
                  value={senderPhone}
                  onChange={setSenderPhone}
                  autoComplete="off"
                  data-testid="sender-phone-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Email"
                  value={senderEmail}
                  onChange={setSenderEmail}
                  type="email"
                  autoComplete="off"
                  data-testid="sender-email-input"
                />
              </div>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Default Package Dimensions</Text>
            <Text variant="bodySm" as="p" tone="subdued">
              Used when product dimensions are not specified
            </Text>
            <Divider />
            <TextField
              label="Weight (kg)"
              value={defaultWeight}
              onChange={setDefaultWeight}
              type="number"
              autoComplete="off"
              data-testid="default-weight-input"
            />
            <InlineStack gap="200">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Length (cm)"
                  value={defaultLength}
                  onChange={setDefaultLength}
                  type="number"
                  autoComplete="off"
                  data-testid="default-length-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Width (cm)"
                  value={defaultWidth}
                  onChange={setDefaultWidth}
                  type="number"
                  autoComplete="off"
                  data-testid="default-width-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Height (cm)"
                  value={defaultHeight}
                  onChange={setDefaultHeight}
                  type="number"
                  autoComplete="off"
                  data-testid="default-height-input"
                />
              </div>
            </InlineStack>
          </BlockStack>
        </Card>

        <Button
          primary
          onClick={handleSaveSettings}
          loading={loading}
          fullWidth
          data-testid="save-settings-btn"
        >
          Save Sender & Package Settings
        </Button>
      </BlockStack>
    </Page>
  );
}

export default SettingsPage;
